// src\themes\choopar\components\StickyStory.tsx
'use client';

import Image from 'next/image';
import { motion } from 'motion/react';

type StoryItem = {
    title?: string;
    subtitle?: string;
    description?: string;
};

type StickyStoryData = {
    title?: string;
    image?: { url: string };
    thumbnail?: { url: string };
    items?: StoryItem[];
};

export default function StickyStory({ data }: { data: StickyStoryData }) {
    const items = data.items || [];
    const image = data.image || data.thumbnail;
    if (!items.length) return null;

    return (
        <div className="col-span-12 px-4 sm:px-6 lg:px-12">
            {data.title && (
                <h2 className="text-xl lg:text-3xl font-light text-primary-900 mb-12 lg:mb-20 tracking-tight">
                    {data.title}
                </h2>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
                {image?.url && (
                    <div className="lg:sticky lg:top-28 h-fit">
                        <div className="relative aspect-[4/5] overflow-hidden rounded-md bg-primary-100">
                            <Image
                                src={image.url}
                                alt={data.title || 'تصویر'}
                                fill
                                className="object-cover"
                            />
                        </div>
                    </div>
                )}

                <div className="flex flex-col divide-y divide-primary-200">
                    {items.map((item, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.5 }}
                            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                            className="py-10 first:pt-0 flex gap-6"
                        >
                            <span className="text-primary-300 text-sm font-primary-bold shrink-0 pt-1">
                                {String(index + 1).padStart(2, '0')}
                            </span>
                            <div className="space-y-2.5">
                                {item.title && (
                                    <h3 className="text-lg text-primary-900 font-primary-bold tracking-tight">
                                        {item.title}
                                    </h3>
                                )}
                                {item.subtitle && (
                                    <p className="text-[11px] tracking-[0.14em] text-primary-500 uppercase">
                                        {item.subtitle}
                                    </p>
                                )}
                                {item.description && (
                                    <p className="text-[13px] text-primary-600 leading-7">
                                        {item.description}
                                    </p>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
}